// ABOUTME: Renders metadata-driven read-only views with grouped fields and tabs.
// ABOUTME: Keeps filtered multi-step tabs aligned with their rendered content.
import React from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import FieldSwitchType from "./FieldSwitchType";
import {
    Box,
    Badge,
    Grid,
    Icon,
    Paper,
    Tab,
    Tabs,
    Typography
} from "@material-ui/core";
import { filterFormGroups } from "customHooks/useCompleoForm/components/FormMain";
import ModalEdit from "../ModalEdit/ModalEdit";
import {
    IUseCompleoViewEditDataParams,
    UseCompleoViewCustomComponents,
    UseCompleoEditCustomComponents
} from "customHooks/useCompleoView";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import {
    resolveMainViewRequestedStepIndex,
    getMainViewVisibleSteps,
    resolveMainViewSelectedStepIndex
} from "./MainViewHelpers";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: "100%",
            padding: theme.spacing(1),
            height: "100%"
        },
        formGroup: {
            width: "100%",
            margin: theme.spacing(1),
            padding: theme.spacing(2)
        },
        formGroupTitle: {
            marginBottom: theme.spacing(1)
        },
        formGroupTypography: {
            fontWeight: 600
        },
        grow: {
            flexGrow: 1
        }
    })
);

interface IProps {
    valuesFromSource: Compleo.IObject;
    metadata: Compleo.IObject[];
    listas: Compleo.IObject;
    t: any;
    language: string;
    formGroups: Compleo.IObject[];
    IsMultiStepForm?: boolean;
    MultiStepForm: Compleo.IObject[];
    editParams?: IUseCompleoViewEditDataParams;
    CustomComponents: UseCompleoViewCustomComponents[];
    CustomEditComponents: UseCompleoEditCustomComponents[];
    formGroupPaperElevation?: number;
    multiStepTabNameToOpen?: string;
    multiStepTabBadges?: Record<string, number>;
    addProps?: Compleo.useCompleoForm.AdditionalPropertiesField[];
    readOnly: boolean;
    fieldsToHide?: string[];
    customCVsData?: Compleo.IObject[];
}

const MainView = (props: IProps) => {
    const {
        valuesFromSource,
        metadata,
        listas,
        t,
        language,
        formGroups,
        IsMultiStepForm = false,
        MultiStepForm,
        editParams,
        CustomComponents,
        CustomEditComponents = [],
        formGroupPaperElevation = 1,
        multiStepTabNameToOpen,
        multiStepTabBadges = {},
        addProps,
        readOnly,
        fieldsToHide = [],
        customCVsData
    } = props;
    const { moduleEditName = "" } = editParams || {};
    const [modalEditOpen, setModalEditOpen] = React.useState(false);
    const [formIdToEdit, setFormIdToEdit] = React.useState<string>("");
    const multiStepTabNameToOpenHandledRef = React.useRef<
        string | undefined
    >(undefined);

    const storageStepName = `MainViewStepdId_${moduleEditName}`;
    const lastStageId = Number(localStorage.getItem(storageStepName) || 0);

    const [currentStepId, setCurrentStepId] =
        React.useState<number>(lastStageId);

    const classes = useStyles();
    const isMultiStep = IsMultiStepForm && MultiStepForm?.length;
    const allFormGroupsFiltered = filterFormGroups(
        formGroups,
        metadata,
        fieldsToHide
    );
    const stepsFormGroups: string[] = allFormGroupsFiltered
        .filter((item: Compleo.IObject) => item.stepId)
        .map((item: Compleo.IObject) => item.stepId);

    const visibleSteps = getMainViewVisibleSteps(
        MultiStepForm,
        stepsFormGroups
    );
    const selectedStepIndex = isMultiStep
        ? resolveMainViewSelectedStepIndex(visibleSteps, currentStepId)
        : currentStepId;
    const currentTabValue =
        MultiStepForm[selectedStepIndex] || MultiStepForm[0];
    // console.log("MultiStepForm", MultiStepForm);

    const stepFormGroups = formGroups.filter(
        (f) => !isMultiStep || f.stepId === currentTabValue.id
    );
    const formGroupsFiltered = filterFormGroups(
        stepFormGroups,
        metadata,
        fieldsToHide
    );
    let lastOrderItem = -1;

    const openModal = (formId: string) => {
        setFormIdToEdit(formId);
        setModalEditOpen(true);
    };

    const handleTabChange = React.useCallback(
        (event: React.ChangeEvent<unknown> | null, newValue: number) => {
            setCurrentStepId(newValue);
            localStorage.setItem(storageStepName, newValue.toString());
        },
        [storageStepName]
    );

    React.useEffect(() => {
        if (isMultiStep && selectedStepIndex !== currentStepId) {
            handleTabChange(null, selectedStepIndex);
        }
    }, [isMultiStep, selectedStepIndex, currentStepId, handleTabChange]);

    React.useEffect(() => {
        if (!multiStepTabNameToOpen) {
            multiStepTabNameToOpenHandledRef.current = undefined;
            return;
        }

        const requestedStepIndex = resolveMainViewRequestedStepIndex(
            visibleSteps,
            multiStepTabNameToOpen,
            multiStepTabNameToOpenHandledRef.current
        );
        if (requestedStepIndex !== undefined) {
            multiStepTabNameToOpenHandledRef.current = multiStepTabNameToOpen;
            handleTabChange(null, requestedStepIndex);
        }
    }, [multiStepTabNameToOpen, visibleSteps, handleTabChange]);

    return (
        <Grid
            container
            className={classes.root}
            // alignItems="flex-start"
            alignContent="flex-start"
            alignItems="stretch"
        >
            {isMultiStep && (
                <Tabs
                    value={selectedStepIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="secondary"
                    variant="scrollable"
                >
                    {visibleSteps.map(({ step: tab, stepIndex }) => {
                        const badgeCount = Number(
                            multiStepTabBadges[tab.id] || 0
                        );

                        return (
                            <Tab
                                key={tab.id}
                                value={stepIndex}
                                label={
                                    <Badge
                                        color="secondary"
                                        badgeContent={badgeCount}
                                        invisible={badgeCount <= 0}
                                    >
                                        <span>{t(`a_StepForm_${tab.id}`)}</span>
                                    </Badge>
                                }
                            />
                        );
                    })}
                </Tabs>
            )}
            {formGroupsFiltered.map((form: Compleo.IObject) => {
                const viewOptionShowOutsideGrid =
                    form.viewOptionShowOutsideGrid === true;
                const filteredMetadata = metadata.filter(
                    (item) =>
                        item.formGroupId === form.id &&
                        !fieldsToHide.includes(item.fieldName)
                );
                const hasFieldsToEdit =
                    filteredMetadata.filter(
                        (item: Compleo.IObject) =>
                            item.viewOptionAllowEdit === true
                    ).length > 0;

                const showEditButton =
                    editParams !== undefined && hasFieldsToEdit;
                const showTopEditButton =
                    t(`a_FormGroup_${form.id}`, "") !== "" && showEditButton;
                const showBottomEditButton =
                    showEditButton && !showTopEditButton;
                const inline = form.viewOptionInline === true;

                if (!viewOptionShowOutsideGrid) {
                    return (
                        <Grid
                            container
                            item
                            xs={form?.viewOptionColumnSize?.xs || 12}
                            sm={form?.viewOptionColumnSize?.sm}
                            md={form?.viewOptionColumnSize?.md}
                            lg={form?.viewOptionColumnSize?.lg}
                            key={form.id}
                        >
                            <Paper
                                elevation={formGroupPaperElevation}
                                className={classes.formGroup}
                            >
                                <Grid container>
                                    {t(`a_FormGroup_${form.id}`, "") !== "" && (
                                        <Grid
                                            item
                                            xs
                                            className={classes.formGroupTitle}
                                        >
                                            <Typography
                                                component="h3"
                                                variant="body1"
                                                align="left"
                                                className={
                                                    classes.formGroupTypography
                                                }
                                            >
                                                {t(`a_FormGroup_${form.id}`)}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {!readOnly && showTopEditButton && (
                                        <Grid
                                            container
                                            item
                                            xs={1}
                                            justify="flex-end"
                                        >
                                            <ButtonIconTooltipCompleo
                                                label={t("COMPLEOGENERAL_Edit")}
                                                onClick={() => {
                                                    openModal(form.id);
                                                }}
                                                size="small"
                                            >
                                                <Icon fontSize="small">
                                                    edit
                                                </Icon>
                                            </ButtonIconTooltipCompleo>
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid container>
                                    <Grid container spacing={1}>
                                        {filteredMetadata.map((m) => {
                                            const lastOrderParam =
                                                lastOrderItem;
                                            lastOrderItem = m.order;

                                            const fieldDef = m;
                                            return (
                                                <MainViewItemData
                                                    key={m.fieldName}
                                                    lastOrderItem={
                                                        lastOrderItem
                                                    }
                                                    lastOrderParam={
                                                        lastOrderParam
                                                    }
                                                    inline={inline}
                                                >
                                                    <FieldSwitchType
                                                        fieldData={
                                                            valuesFromSource
                                                        }
                                                        fieldName={m.fieldName}
                                                        type={
                                                            fieldDef?.elementType
                                                        }
                                                        t={t}
                                                        language={language}
                                                        fieldDef={fieldDef}
                                                        listas={listas}
                                                        inline={inline}
                                                        completeMetadata={
                                                            metadata
                                                        }
                                                        CustomComponents={
                                                            CustomComponents
                                                        }
                                                        addProps={addProps}
                                                        customCVsData={
                                                            customCVsData
                                                        }
                                                        applicantId={
                                                            editParams?.keyData
                                                                ?.id
                                                        }
                                                    />
                                                </MainViewItemData>
                                            );
                                        })}
                                        {!readOnly && showBottomEditButton && (
                                            <>
                                                <Box flexGrow={1} />
                                                <Box
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    alignSelf="center"
                                                >
                                                    <ButtonIconTooltipCompleo
                                                        label="Edit"
                                                        onClick={() => {
                                                            openModal(form.id);
                                                        }}
                                                        size="small"
                                                    >
                                                        <Icon fontSize="small">
                                                            edit
                                                        </Icon>
                                                    </ButtonIconTooltipCompleo>
                                                </Box>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                                {/* {showBottomEditButton && (
                                <Grid container item xs={12} justify="flex-end">
                                    <ButtonIconTooltipCompleo
                                        label="Edit"
                                        onClick={() => {
                                            openModal(form.id);
                                        }}
                                        size="small"
                                    >
                                        <Icon fontSize="small">edit</Icon>
                                    </ButtonIconTooltipCompleo>
                                </Grid>
                            )} */}
                            </Paper>
                        </Grid>
                    );
                } else {
                    return (
                        <>
                            {filteredMetadata.map((m) => {
                                const fieldDef = m;

                                return (
                                    <FieldSwitchType
                                        key={m.fieldName}
                                        fieldData={valuesFromSource}
                                        fieldName={m.fieldName}
                                        type={fieldDef?.elementType}
                                        t={t}
                                        language={language}
                                        fieldDef={fieldDef}
                                        listas={listas}
                                        inline={inline}
                                        completeMetadata={metadata}
                                        CustomComponents={CustomComponents}
                                        addProps={addProps}
                                    />
                                );
                            })}
                        </>
                    );
                }
            })}
            {!readOnly && editParams && modalEditOpen && (
                <ModalEdit
                    open={modalEditOpen}
                    onClose={() => setModalEditOpen(false)}
                    data={valuesFromSource}
                    formId={formIdToEdit}
                    editParams={editParams}
                    CustomEditComponents={CustomEditComponents}
                />
            )}
        </Grid>
    );
};

interface IItemData {
    children: any;
    lastOrderParam: number;
    lastOrderItem: number;
    inline?: boolean;
}

export const MainViewItemData = (props: IItemData) => {
    const { children, lastOrderParam, lastOrderItem, inline = false } = props;

    if (
        lastOrderParam !== lastOrderItem &&
        lastOrderParam !== -1 &&
        inline === false
    ) {
        return (
            <>
                <Grid container></Grid>
                {children}
            </>
        );
    } else {
        return <>{children}</>;
    }
};

export default MainView;
